// src/oura/newscan/towns.ts
//
// The towns city-wide questions may name, and the rule that an outlet's area
// is never one of them (aeo_visibility_spec.json city_rule.towns and
// city_rule.anchor_is_not_a_town, 15 Oct 2026).
//
// The outlet area (the anchor) is the sub-area a customer names for the
// doorstep: SS15, USJ Taipan, Section 17, Damansara Uptown. The town is the
// ring above it. An outlet named after its own town ("Subang Jaya" in Subang
// Jaya) makes about 11 near-repeats, because its location questions and the
// city-wide questions name the same place. When the business genuinely has no
// smaller area, the town stays the anchor and the city-wide questions ask the
// wider ring customers type (KL for a PJ clinic).
//
// Used by the Business step (the card that asks for the sub-area) and the
// fixture engine (which refuses such an anchor, as the backend does).

import type { ScanDraftOutlet } from "@service/aeo/scanDraftTypes";

/** city_rule.towns, as customers type them. */
export const CITY_TOWNS = [
    "KL",
    "PJ",
    "Subang Jaya",
    "Shah Alam",
    "Putrajaya",
    "Klang",
    "Cheras",
    "Johor Bahru",
    "Penang",
    "Ipoh",
    "Melaka",
    "Seremban",
    "Kota Kinabalu",
    "Kuching",
] as const;

/** city_rule.region_phrase: only where research shows customers type it. */
export const REGION = "Klang Valley";

/** city_rule.towns plus Klang Valley: the towns the anchor rule and the lint know. */
export const TOWN_CHOICES: string[] = [...CITY_TOWNS, REGION];

/**
 * What the Town picker on each outlet offers, as customers type them ("dentist
 * in Puchong"). A state is never a town: "Selangor" is too wide for a location
 * question. Staff can still type a town that is not here. The backend takes
 * any town an outlet carries, so a longer list needs no backend change.
 */
export const TOWN_GROUPS: { label: string; towns: string[] }[] = [
    {
        label: "Klang Valley",
        towns: [
            "KL", "PJ", "Subang Jaya", "Shah Alam", "Cheras", "Puchong", "Ampang", "Klang", "Putrajaya", "Cyberjaya",
            "Kajang", "Bangi", "Seri Kembangan", "Sungai Buloh", "Selayang", "Rawang", "Semenyih", "Damansara", REGION,
        ],
    },
    {
        label: "Rest of Malaysia",
        towns: [
            "Johor Bahru", "Iskandar Puteri", "Kulai", "Batu Pahat", "Muar", "Kluang",
            "Melaka", "Seremban", "Nilai", "Port Dickson",
            "Penang", "George Town", "Butterworth", "Bukit Mertajam",
            "Ipoh", "Taiping", "Kuantan", "Bentong", "Genting Highlands", "Cameron Highlands",
            "Kota Bharu", "Kuala Terengganu", "Alor Setar", "Sungai Petani", "Langkawi",
            "Kota Kinabalu", "Sandakan", "Tawau", "Kuching", "Miri", "Sibu",
        ],
    },
];

/** Every town the picker offers, in its order. */
export const TOWN_LIST: string[] = TOWN_GROUPS.flatMap((g) => g.towns);

/** Other spellings staff and customers write, for the picker only (not the anchor rule). Lower case keys. */
const PICKER_ALIASES: Record<string, string> = {
    jb: "Johor Bahru",
    kk: "Kota Kinabalu",
    malacca: "Melaka",
    georgetown: "George Town",
    "bandar baru bangi": "Bangi",
    "kuala lumpur city": "KL",
    "wilayah persekutuan": "KL",
};

/** Longer names customers also write, keyed in lower case. */
const ALIASES: Record<string, string> = {
    "petaling jaya": "PJ",
    "kuala lumpur": "KL",
};

/** The town a name stands for, as city_rule writes it ("Petaling Jaya" is PJ), or null when it is not a town. */
export function canonicalTown(name: string): string | null {
    const key = name.trim().replace(/\s+/g, " ").toLowerCase();
    if (!key) return null;
    if (ALIASES[key]) return ALIASES[key];
    return TOWN_CHOICES.find((t) => t.toLowerCase() === key) ?? null;
}

/**
 * The picker's spelling of a town ("Kuala Lumpur" is KL, "JB" is Johor
 * Bahru), or null when the picker does not list it. Wider than canonicalTown,
 * which stays on the anchor rule's towns.
 */
export function knownTown(name: string): string | null {
    const own = canonicalTown(name);
    if (own) return own;
    const key = name.trim().replace(/\s+/g, " ").toLowerCase();
    if (!key) return null;
    return PICKER_ALIASES[key] ?? TOWN_LIST.find((t) => t.toLowerCase() === key) ?? null;
}

/** States and federal territories: never a town for location questions ("dentist in Selangor" is too wide). Lower case. */
const STATES = new Set([
    "selangor", "johor", "kedah", "kelantan", "negeri sembilan", "pahang", "perak", "perlis", "pulau pinang",
    "sabah", "sarawak", "terengganu", "wilayah persekutuan", "labuan", "malaysia",
]);

/** Whether a name is a state (or the country), not a town. A listed town of the same name wins ("Melaka", "Penang"). */
export function isStateName(name: string): boolean {
    const key = name.trim().replace(/\s+/g, " ").toLowerCase();
    return STATES.has(key) && !knownTown(key);
}

/** A town an outlet can carry: its listed spelling, or "" when blank or a state. */
export function usableTown(name: string): string {
    return name.trim() && !isStateName(name) ? townSpelling(name) : "";
}

/** A town as the picker writes it: its listed spelling when it has one, else as typed (tidied). */
export function townSpelling(name: string): string {
    const tidy = name.trim().replace(/\s+/g, " ");
    return knownTown(tidy) ?? tidy;
}

/** Whether two names are the same town, aliases included. */
export function sameTown(a: string, b: string): boolean {
    const x = canonicalTown(a);
    return x !== null && x === canonicalTown(b);
}

/**
 * The town an outlet is named after, when its own town is that same town and
 * staff have not said it has no smaller area. Null: the anchor is fine.
 * `city` stands in for a blank town, as the drafting does.
 */
export function anchorTown(outlet: Pick<ScanDraftOutlet, "name" | "town" | "noSmallerArea">, city = ""): string | null {
    if (outlet.noSmallerArea === true) return null;
    const own = canonicalTown(outlet.name);
    if (!own) return null;
    const town = outlet.town.trim() || city.trim() || outlet.name;
    return canonicalTown(town) === own ? own : null;
}

/**
 * The town a sub-area sits in, for the areas customers name most ("Bangsar"
 * is in KL, "SS15" in Subang Jaya). Keys in lower case. An area that is a
 * town itself is read by canonicalTown first.
 */
const AREA_TOWN: Record<string, string> = {
    bangsar: "KL", "mont kiara": "KL", "bukit bintang": "KL", "sri hartamas": "KL", "desa parkcity": "KL", "bukit jalil": "KL",
    "taman tun dr ismail": "KL", ttdi: "KL", kepong: "KL", setapak: "KL", "wangsa maju": "KL", "old klang road": "KL", "jalan ipoh": "KL",
    "section 17": "PJ", "damansara uptown": "PJ", ss2: "PJ", "damansara utama": "PJ", "kota damansara": "PJ", "ara damansara": "PJ", "bandar utama": "PJ",
    subang: "Subang Jaya", ss15: "Subang Jaya", "usj taipan": "Subang Jaya", usj: "Subang Jaya", "subang parade area": "Subang Jaya",
    "section 7": "Shah Alam", "setia alam": "Shah Alam",
    "bandar puchong": "Puchong", "bandar puchong jaya": "Puchong", "bandar kinrara": "Puchong", "puchong jaya": "Puchong",
    "pandan indah": "Ampang", "pandan jaya": "Ampang", "taman melawati": "Ampang",
    "janda baik": "Bentong",
};

/**
 * The town city-wide questions should ask for an outlet in this area: the
 * area's own town when it is one ("Petaling Jaya" is PJ), else the town the
 * sub-area sits in. Null when Oura does not know it.
 */
export function townForArea(area: string): string | null {
    const own = canonicalTown(area);
    if (own) return own;
    return AREA_TOWN[area.trim().replace(/\s+/g, " ").toLowerCase()] ?? null;
}

/**
 * The town for a new or renamed outlet on the Town picker: townForArea, then
 * a town the picker lists ("Puchong"), then a town at the end of the name
 * ("Seksyen 18 Shah Alam" is in Shah Alam). Null when none.
 */
export function townForPlace(name: string): string | null {
    const found = townForArea(name) ?? knownTown(name);
    if (found) return found;
    const low = ` ${name.trim().replace(/\s+/g, " ").toLowerCase()}`;
    const tail = [...TOWN_LIST].sort((a, b) => b.length - a.length).find((t) => t.length > 2 && low.endsWith(` ${t.toLowerCase()}`));
    return tail ?? null;
}

/** A few sub-areas customers name, as examples for the field. */
export const SUB_AREA_EXAMPLES: Record<string, string[]> = {
    "Subang Jaya": ["SS15", "USJ Taipan", "Subang Parade area"],
    PJ: ["Section 17", "Damansara Uptown", "SS2"],
    "Shah Alam": ["Section 7", "Setia Alam"],
    KL: ["Bangsar", "Mont Kiara", "Cheras"],
};

/**
 * The wider ring city-wide questions can ask when the town has no smaller
 * area. KL for PJ and the KL suburbs; KL or Klang Valley for Subang Jaya and
 * Shah Alam, Klang Valley only where customers type it. Empty: none.
 */
export const WIDER_RING: Record<string, string[]> = {
    PJ: ["KL"],
    Cheras: ["KL"],
    "Subang Jaya": ["KL", REGION],
    "Shah Alam": ["KL", REGION],
};

export function widerRing(town: string): string[] {
    const own = canonicalTown(town);
    return own ? WIDER_RING[own] ?? [] : [];
}
